import React from "react";

import { isValidKey } from "../../utils/functional";
import { concatClassNames } from "../../utils/styles";
import { HeroLayout } from "./HeroLayout";
import { HeroLayoutProps } from "./HeroLayout.interfaces";

import styles from "./HeroSet.module.scss";
import defaultBreakpointStyles from "./HeroSetDefaultBreakpoints.module.scss";

/**
 * Order matters, put your smallest breakpoint on top
 *
 * Designs by thelab tend to have desktop and mobile designs most frequently.
 * Laptop and tiny breakpoints might not be relevant here but I wanted to start
 * with the option for now.
 */
const defaultBreakpoints = [
    "tiny",
    "mobile",
    "tablet",
    "laptop",
    "desktop",
] as const;

type DefaultBreakpoint = (typeof defaultBreakpoints)[number];

/**
 * Forces the use of DefaultBreakpoint values unless something else is
 * explicitly specified
 */
type Breakpoint<T extends string> = T extends DefaultBreakpoint
    ? DefaultBreakpoint
    : T;

export interface HeroSetProps<T extends string = DefaultBreakpoint> {
    /**
     * Pass in a series of layout settings. Not sure if it's better to do it this way
     * or to have it pass a series of React nodes; the latter would make this
     * usable outside of Flexihero, which might be interesting.
     */
    layouts: Partial<Record<T, HeroLayoutProps>>;
    /** If you provide breakpoints, then they'll enforce what goes in `layouts`. */
    breakpoints?: readonly T[];
    /**
     * If you provide breakpoints, then you'll need to bring your own styles.
     * Or, if you don't like the default styles' values, then you can override!
     */
    breakpointStyles?: Record<string, string>;
}

/**
 * A HeroSet is a collection of HeroLayouts that can be shown at different
 * browser widths.
 *
 * Hero designs usually require different designs at different breakpoints:
 * for example, an image might be to the left of content on desktop and above
 * content on mobile. In addition, hero designs often require different media
 * or content: Perhaps an image that's optimized for mobile, or perhaps more
 * truncated content.
 *
 * ### Goals
 * - Make an implementation that can be rendered server-side and not cause a
 *   layout shift
 * - Provide a sensible default but have a way to override if necessary
 *
 * ### Assumptions
 * - When a HeroSet is present, something will always want to be visible. This
 *   doesn't have a concept of "show on mobile and laptop, but not tablet".
 *
 * ### Logic
 * - hide all layouts by default
 * - breakpoints are ordered to go smallest breakpoint to biggest
 * - iterate through breakpoints that have layouts named in props
 * - make the first hit visible from zero to its max
 * - make the next hit visible from the previous hit's max to its max
 * - unless it's the last one, then it gets a class that only sets a
 *   min-width of the previous hit's max
 */
export const HeroSet = <T extends string = DefaultBreakpoint>(
    props: HeroSetProps<Breakpoint<T>>,
) => {
    /** IMPORTANT! Never change from `"zero"` without updating HeroSetMixins */
    let lastActive = "zero";

    const breakpoints = props.breakpoints ?? defaultBreakpoints;
    const breakpointStyles = props.breakpointStyles ?? defaultBreakpointStyles;

    const filteredBreakpoints = breakpoints.filter(
        (key): key is Breakpoint<T> => key in props.layouts,
    );

    const layoutLength = filteredBreakpoints.length;

    // A bit superfluous but figured it'd be good to explicitly account for this
    if (layoutLength === 0) return <></>;

    // No need for HeroSet extras at all in this case
    if (layoutLength === 1) {
        const layout = props.layouts[filteredBreakpoints[0]];
        if (layout?.content && layout.media) return <HeroLayout {...layout} />;
    }

    const layouts = filteredBreakpoints.map((key, index) => {
        const titleCase = `${key.charAt(0).toUpperCase()}${key.substring(1)}`;
        if (!props.layouts[key]) return null;
        const _lastActive = lastActive;
        lastActive = key;

        const toKey = `${_lastActive}To${titleCase}`;
        const toClass = isValidKey(breakpointStyles, toKey)
            ? breakpointStyles[toKey]
            : "";

        const andUpKey = `${_lastActive}AndUp`;
        const andUpClass = isValidKey(breakpointStyles, andUpKey)
            ? breakpointStyles[andUpKey]
            : "";

        const layoutStyles = [
            props.layouts[key].styles?.rootClassName ?? "",
            styles.heroSetLayout,
            index + 1 !== layoutLength ? toClass : andUpClass,
        ];
        return (
            <HeroLayout
                key={key}
                {...props.layouts[key]}
                styles={{
                    ...props.layouts[key].styles,
                    rootClassName: concatClassNames(layoutStyles),
                }}
            />
        );
    });

    return <>{layouts}</>;
};
